import { useEffect, useState } from 'react';
import { Outlet, useOutletContext, useParams } from 'react-router-dom';
import {
  useQueryState,
  IconLoader,
  SchemaFilterType,
  ButtonPrimary,
  IconExport,
  ButtonSize,
  SortDir,
  usePrevious,
  PermissionEnum,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  setAccountId as setAccountIdAction,
  clearCardsTransactionState,
} from '../store/slices/statements';
import { overviewAccountsSchema } from '../variables/overview-accounts';
import OverviewStatementsTransaction from '../../../components/partial/overview-statements/overview-statements-trancations';
import { CustomerType, StatementsContextType } from '../types';
import useStatements from '../hooks/use-statements';
import StatementPanel from './statements-panel';

import cn from '../customers.module.css';

const OverviewStatementsTransactions = ({
  customerType,
}: {
  customerType: CustomerType;
}) => {
  const { accounts, currentAccount, balances } =
    useOutletContext<StatementsContextType>();

  const prevAccount = usePrevious(currentAccount);

  const dispatch = useAppDispatch();

  const { customerId } = useParams();

  const { accountsTransactionsStatements } = useAppSelector(
    (state) => state.customerStatements,
  );
  const { baseUserInformation } = useAppSelector(
    (state) => state.individualCustomers.info.overview,
  );
  const { activePermissions } = useAppSelector(
    (state) => state.customersDictionaries,
  );

  const [statementPanelIsVisible, setStatementPanelIsVisible] = useState(false);

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(
    overviewAccountsSchema,
  );

  const {
    setAccountId,
    isLoading,
    isFetching,
    onSort,
    onFilter,
    formattedData,
    accountId,
    errorList,
    statementError,
  } = useStatements({
    statements: accountsTransactionsStatements,
    filters,
    onSortChange,
    onFilterChange,
    sort,
    customerId,
    customerType,
    accountNumber: currentAccount?.fullNumber,
  });

  useEffect(() => {
    if (accounts && accounts.length && !currentAccount) {
      setAccountId(accounts[0].id);
      dispatch(setAccountIdAction(accounts[0].id));

      onFilter({
        ...filters,
        account: {
          value: [accounts[0].fullNumber],
          type: SchemaFilterType.STRING,
        },
        sortProp: {
          value: ['transferDate'],
          type: SchemaFilterType.STRING,
        },
        sortDir: {
          value: [SortDir.DESC],
          type: SchemaFilterType.STRING,
        },
      });
    } else if (currentAccount && currentAccount !== prevAccount) {
      setAccountId(currentAccount.id);
      dispatch(setAccountIdAction(currentAccount.id));

      onFilter({
        ...filters,
        account: {
          value: [currentAccount.fullNumber],
          type: SchemaFilterType.STRING,
        },
        sortProp: {
          value: ['transferDate'],
          type: SchemaFilterType.STRING,
        },
        sortDir: {
          value: [SortDir.DESC],
          type: SchemaFilterType.STRING,
        },
      });
    }
  }, [accounts, filters, dispatch, currentAccount, prevAccount]);

  useEffect(() => {
    dispatch(clearCardsTransactionState());
  }, [dispatch]);

  let content;

  if (isLoading) {
    content = (
      <div className={cn.overviewStatementsLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <OverviewStatementsTransaction
        onSort={onSort}
        onFilter={onFilter}
        data={formattedData}
        filters={filters}
        accountId={accountId}
        sort={sort}
        requestInProgress={isFetching}
        errorList={errorList}
      />
    );
  }

  return (
    <>
      {(customerType !== 'individual' ||
        (customerType === 'individual' &&
          baseUserInformation?.type !== 'COMPANY_OFFICIAL')) && (
        <>
          {accounts && (
            <div className={cn.overviewStatementsAccountsWrapper}>
              {accounts.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`${cn.overviewStatementsAccountButton} ${
                    accountId === item.id
                      ? cn.overviewStatementsAccountButtonActive
                      : ''
                  }`}
                  onClick={() => {
                    setAccountId(item.id);
                    dispatch(setAccountIdAction(item.id));

                    onFilter({
                      ...filters,
                      account: {
                        value: [item.fullNumber],
                        type: SchemaFilterType.STRING,
                      },
                      sortProp: {
                        value: ['transferDate'],
                        type: SchemaFilterType.STRING,
                      },
                      sortDir: {
                        value: [SortDir.DESC],
                        type: SchemaFilterType.STRING,
                      },
                    });
                  }}
                >
                  {item.fullNumber}
                </button>
              ))}
            </div>
          )}

          <div className={cn.accountsStatementsControls}>
            {activePermissions?.includes(
              PermissionEnum.ACCOUNTS_STATEMENTS_EXPORT_ACTION,
            ) && (
              <ButtonPrimary
                icon={<IconExport />}
                size={ButtonSize.SMALL}
                text="Export"
                className={cn.exportButton}
                onClick={() => setStatementPanelIsVisible(true)}
              />
            )}
            {statementError && (
              <div className={cn.downloadStatementError}>{statementError}</div>
            )}
          </div>
          <div
            className={`${cn.overviewStatementsContentTable} ${
              isFetching ? cn.tableLoading : ''
            }`}
          >
            {content}
          </div>
          <StatementPanel
            statementPanelVisible={statementPanelIsVisible}
            onCloseStatementPanel={() => setStatementPanelIsVisible(false)}
            selectedAccount={currentAccount?.id}
            accounts={accounts}
            currencies={balances
              ?.filter((item) => item.accountId === currentAccount?.id)
              ?.map((item) => item.currencyId)}
          />
        </>
      )}

      <Outlet />
    </>
  );
};

export default OverviewStatementsTransactions;
